import React, { useState, useEffect } from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// API Configuration
import API_BASE_URL from "config/api";

// API Passcode for admin operations (domain management only)
const API_PASSCODE = process.env.REACT_APP_API_PASSCODE || "";

// Available Public APIs
const PUBLIC_APIS = [
  {
    name: "List Books",
    method: "GET",
    endpoint: "/public-library/list",
    description: "Returns list of all books with share URLs",
    params: "None (domain must be whitelisted)",
  },
  {
    name: "Get Single Book",
    method: "GET",
    endpoint: "/public-library/:bookId",
    description: "Returns details of a specific book by ID",
    params: "bookId (path)",
  },
  {
    name: "Student Analytics",
    method: "GET",
    endpoint: "/activity/user-summary",
    description: "Returns user activity summary and analytics",
    params: "username (query)",
  },
];

function ApiManagement() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Fetch whitelisted domains
  const fetchDomains = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/domains/list?passcode=${API_PASSCODE}`);
      if (response.data.success) {
        setDomains(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching domains:", error);
      showSnackbar("Failed to load whitelisted domains", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  // Add new domain
  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      showSnackbar("Please enter a domain", "warning");
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/domains/add?passcode=${API_PASSCODE}`, {
        domain: newDomain,
        description: newDescription,
      });
      if (response.data.success) {
        showSnackbar("Domain whitelisted successfully", "success");
        setNewDomain("");
        setNewDescription("");
        setDialogOpen(false);
        fetchDomains();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || "Failed to add domain";
      showSnackbar(errorMsg, "error");
    }
  };

  // Remove domain
  const handleRemoveDomain = async (id) => {
    if (!window.confirm("Are you sure you want to remove this domain?")) return;

    try {
      const response = await axios.delete(`${API_BASE_URL}/domains/${id}?passcode=${API_PASSCODE}`);
      if (response.data.success) {
        showSnackbar("Domain removed", "success");
        fetchDomains();
      }
    } catch (error) {
      showSnackbar("Failed to remove domain", "error");
    }
  };

  // Toggle domain active status
  const handleToggleDomain = async (id) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/domains/${id}/toggle?passcode=${API_PASSCODE}`
      );
      if (response.data.success) {
        showSnackbar(response.data.message, "success");
        fetchDomains();
      }
    } catch (error) {
      showSnackbar("Failed to toggle domain status", "error");
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar("Copied to clipboard!", "info");
  };

  // Show snackbar
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Domain table data
  const domainTableData = {
    columns: [
      { Header: "Domain", accessor: "domain", width: "30%" },
      { Header: "Description", accessor: "description", width: "25%" },
      { Header: "Status", accessor: "status", width: "15%", align: "center" },
      { Header: "Added", accessor: "added", width: "15%" },
      { Header: "Actions", accessor: "actions", width: "15%", align: "center" },
    ],
    rows: domains.map((d) => ({
      domain: (
        <MDTypography variant="button" fontWeight="medium">
          {d.domain}
        </MDTypography>
      ),
      description: (
        <MDTypography variant="caption" color="text">
          {d.description || "-"}
        </MDTypography>
      ),
      status: (
        <Switch
          checked={d.isActive}
          onChange={() => handleToggleDomain(d._id || d.id)}
          color="success"
        />
      ),
      added: (
        <MDTypography variant="caption" color="text">
          {new Date(d.createdAt).toLocaleDateString()}
        </MDTypography>
      ),
      actions: (
        <IconButton color="error" size="small" onClick={() => handleRemoveDomain(d._id || d.id)}>
          <Icon>delete</Icon>
        </IconButton>
      ),
    })),
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* API Documentation Section */}
        <MDBox mb={4}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Public API Endpoints
              </MDTypography>
            </MDBox>
            <MDBox p={3}>
              <MDTypography variant="body2" color="text" mb={2}>
                These APIs can be accessed by whitelisted domains or by providing a valid passcode.
              </MDTypography>
              <Grid container spacing={2}>
                {PUBLIC_APIS.map((api, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card sx={{ p: 2, bgcolor: "grey.900" }}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <MDTypography variant="h6" fontWeight="medium">
                          {api.name}
                        </MDTypography>
                        <MDBox
                          px={1.5}
                          py={0.5}
                          borderRadius="md"
                          bgcolor={api.method === "GET" ? "success.main" : "warning.main"}
                        >
                          <MDTypography variant="caption" color="white" fontWeight="bold">
                            {api.method}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      <MDBox display="flex" alignItems="center" mb={1}>
                        <MDTypography
                          variant="button"
                          color="info"
                          fontFamily="monospace"
                          sx={{ wordBreak: "break-all" }}
                        >
                          {API_BASE_URL}
                          {api.endpoint}
                        </MDTypography>
                        <Tooltip title="Copy URL">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(`${API_BASE_URL}${api.endpoint}`)}
                          >
                            <Icon fontSize="small">content_copy</Icon>
                          </IconButton>
                        </Tooltip>
                      </MDBox>
                      <MDTypography variant="caption" color="text">
                        {api.description}
                      </MDTypography>
                      <MDBox mt={1}>
                        <MDTypography variant="caption" color="secondary">
                          <strong>Params:</strong> {api.params}
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </MDBox>
          </Card>
        </MDBox>

        {/* Domain Whitelist Section */}
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="success"
            borderRadius="lg"
            coloredShadow="success"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              Whitelisted Domains
            </MDTypography>
            <MDButton
              variant="contained"
              color="white"
              size="small"
              onClick={() => setDialogOpen(true)}
            >
              <Icon sx={{ mr: 1 }}>add</Icon>
              Add Domain
            </MDButton>
          </MDBox>
          <MDBox p={3}>
            <MDTypography variant="body2" color="text" mb={2}>
              Domains listed here can access the public APIs without providing a passcode.
            </MDTypography>
            {loading ? (
              <MDTypography variant="body2" textAlign="center">
                Loading...
              </MDTypography>
            ) : domains.length === 0 ? (
              <MDTypography variant="body2" color="text" textAlign="center">
                No domains whitelisted yet. Click &quot;Add Domain&quot; to whitelist a domain.
              </MDTypography>
            ) : (
              <DataTable
                table={domainTableData}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={true}
                noEndBorder
              />
            )}
          </MDBox>
        </Card>
      </MDBox>

      {/* Add Domain Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "grey.900",
            color: "white",
          },
        }}
      >
        <DialogTitle sx={{ color: "white" }}>Add Whitelisted Domain</DialogTitle>
        <DialogContent>
          <MDBox mt={2}>
            <MDInput
              fullWidth
              label="Domain (e.g., example.com)"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="example.com"
            />
          </MDBox>
          <MDBox mt={2}>
            <MDInput
              fullWidth
              label="Description (optional)"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Partner company website"
            />
          </MDBox>
          <MDTypography variant="caption" color="text" mt={2}>
            Note: Subdomains will also be allowed (e.g., api.example.com)
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDialogOpen(false)} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleAddDomain} color="success" variant="gradient">
            Add Domain
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default ApiManagement;
